
import { useThemeStyles } from '@/context/theme-context'

interface ThemeBlobProps {
  id: string
  path: string
  colorVariant: 'primary' | 'secondary' | 'tertiary' | 'accent' | 'mixed'
  opacity?: number
  className?: string
}

export function ThemeBlob({ id, path, colorVariant, opacity = 0.3, className }: ThemeBlobProps) {
  const { colors } = useThemeStyles()

  const getGradientStops = () => {
    switch (colorVariant) {
      case 'primary':
        return [
          { offset: '0%', color: `hsl(${colors.brand.primary})`, opacity: opacity },
          { offset: '50%', color: `hsl(${colors.brand.primary})`, opacity: opacity * 0.8 },
          { offset: '100%', color: `hsl(${colors.brand.primary})`, opacity: opacity * 0.6 }
        ]
      case 'secondary':
        return [
          { offset: '0%', color: `hsl(${colors.brand.secondary})`, opacity: opacity },
          { offset: '50%', color: `hsl(${colors.brand.secondary})`, opacity: opacity * 0.8 },
          { offset: '100%', color: `hsl(${colors.brand.secondary})`, opacity: opacity * 0.6 }
        ]
      case 'tertiary':
        return [
          { offset: '0%', color: `hsl(${colors.brand.tertiary})`, opacity: opacity },
          { offset: '50%', color: `hsl(${colors.brand.tertiary})`, opacity: opacity * 0.8 },
          { offset: '100%', color: `hsl(${colors.brand.tertiary})`, opacity: opacity * 0.6 }
        ]
      case 'accent':
        return [
          { offset: '0%', color: `hsl(${colors.text.accent})`, opacity: opacity },
          { offset: '50%', color: `hsl(${colors.text.accent})`, opacity: opacity * 0.8 },
          { offset: '100%', color: `hsl(${colors.text.accent})`, opacity: opacity * 0.6 }
        ]
      case 'mixed':
        return [
          { offset: '0%', color: `hsl(${colors.brand.primary})`, opacity: opacity },
          { offset: '50%', color: `hsl(${colors.brand.secondary})`, opacity: opacity * 0.8 },
          { offset: '100%', color: `hsl(${colors.brand.tertiary})`, opacity: opacity * 0.6 }
        ]
      default:
        return [
          { offset: '0%', color: `hsl(${colors.brand.primary})`, opacity: opacity },
          { offset: '100%', color: `hsl(${colors.brand.secondary})`, opacity: opacity * 0.6 }
        ]
    }
  }

  const gradientStops = getGradientStops()

  return (
    <svg viewBox="0 0 200 200" className={className}>
      <defs>
        <linearGradient id={id} x1="0%" y1="0%" x2="100%" y2="100%">
          {gradientStops.map((stop, index) => (
            <stop
              key={index}
              offset={stop.offset}
              stopColor={stop.color}
              stopOpacity={stop.opacity}
            />
          ))}
        </linearGradient>
      </defs>
      <path
        fill={`url(#${id})`}
        d={path}
        transform="translate(100 100)"
      />
    </svg>
  )
}

// Pre-defined blob paths for consistency
export const BLOB_PATHS = {
  blob1: "M44.1,-76.3C58.9,-69.9,73.8,-60.8,81.3,-47.5C88.8,-34.2,88.9,-17.1,85.9,-1.7C82.9,13.6,76.8,27.2,68.4,39.2C60,51.2,49.3,61.6,37,68.8C24.7,76,12.3,80,0,80C-12.4,80,-24.8,76,-36.2,69.7C-47.6,63.4,-58,54.8,-65.8,43.8C-73.6,32.8,-78.8,19.4,-81.2,5.1C-83.6,-9.2,-83.2,-24.4,-77.4,-37.2C-71.6,-50,-60.4,-60.4,-47.3,-67.7C-34.2,-75,-17.1,-79.2,-0.6,-78.2C15.9,-77.2,31.8,-71,44.1,-76.3Z",
  blob2: "M37.8,-65.5C49.3,-58.2,58.7,-46.8,65.1,-33.8C71.5,-20.8,74.9,-6.2,74.8,8.5C74.7,23.2,71.1,38,63.8,50.2C56.5,62.4,45.5,72,32.8,76.8C20.1,81.6,5.7,81.6,-9.2,79.8C-24.1,78,-39.5,74.4,-52.3,66.9C-65.1,59.4,-75.3,48,-80.8,34.8C-86.3,21.6,-87.1,6.6,-84.4,-7.2C-81.7,-21,-75.5,-33.6,-66.2,-44.2C-56.9,-54.8,-44.5,-63.4,-31.2,-69.8C-17.9,-76.2,-3.7,-80.4,9.8,-76.8C23.3,-73.2,26.3,-72.8,37.8,-65.5Z",
  blob3: "M54.2,-78.8C69.8,-71.2,81.5,-54.2,87.1,-35.1C92.7,-16,92.2,5.2,86.8,24.1C81.4,43,71.1,59.6,57.2,71.8C43.3,84,25.8,91.8,6.8,91.2C-12.2,90.6,-32.7,81.6,-48.9,68.4C-65.1,55.2,-77,37.8,-82.4,18.2C-87.8,-1.4,-86.7,-23.2,-79.1,-41.8C-71.5,-60.4,-57.4,-75.8,-41.2,-82.8C-25,-89.8,-6.6,-88.4,10.8,-84.2C28.2,-80,44.6,-86.4,54.2,-78.8Z",
  blob4: "M46.8,-73.2C60.4,-65.8,71.2,-51.4,76.8,-35.2C82.4,-19,82.8,-1,79.2,15.8C75.6,32.6,68,48.2,57.2,60.8C46.4,73.4,32.4,83,16.8,86.2C1.2,89.4,-16,86.2,-31.6,79.4C-47.2,72.6,-61.2,62.2,-70.4,48.2C-79.6,34.2,-84,16.6,-83.8,-0.8C-83.6,-18.2,-78.8,-35.4,-69.8,-49.8C-60.8,-64.2,-47.6,-75.8,-33.2,-82.4C-18.8,-89,-9.4,-90.6,2.2,-94.2C13.8,-97.8,33.2,-80.6,46.8,-73.2Z",
  blob5: "M39.2,-67.1C51.8,-60.2,63.5,-49.8,70.4,-36.8C77.3,-23.8,79.4,-8.2,77.8,6.8C76.2,21.8,71,36.2,62.4,48.2C53.8,60.2,41.8,69.8,28.2,75.2C14.6,80.6,-0.6,81.8,-16.2,79.2C-31.8,76.6,-47.8,70.2,-59.8,59.8C-71.8,49.4,-79.8,35,-82.2,19.2C-84.6,3.4,-81.4,-13.8,-74.6,-29.2C-67.8,-44.6,-57.4,-58.2,-44.2,-64.8C-31,-71.4,-15.5,-70.9,0.8,-72.2C17.1,-73.5,26.6,-74,39.2,-67.1Z"
} as const

export type BlobPathKey = keyof typeof BLOB_PATHS